// External packages
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Hooks
import { useToast } from 'hooks/useToast';
import { API_BASE_URL, authHeaders } from './base';

export interface ApiKey {
  id: string;
  name: string | null;
  keyPrefix: string;
  active: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

/** Returned only once, immediately after creation. */
export interface CreatedApiKey extends ApiKey {
  key: string;
}

export async function getApiKeys(): Promise<ApiKey[]> {
  const response = await fetch(`${API_BASE_URL}/api-keys`, {
    headers: authHeaders(),
  });

  if (!response.ok) throw new Error('Failed to fetch API keys');

  const result = await response.json();
  if (result.error) throw new Error(result.message || 'Failed to fetch API keys');

  return result.data as ApiKey[];
}

export const useGetApiKeys = () => {
  return useQuery({
    queryKey: ['api-keys'],
    queryFn: getApiKeys,
    initialData: [] as ApiKey[],
  });
};

export async function createApiKey(name?: string): Promise<CreatedApiKey> {
  const response = await fetch(`${API_BASE_URL}/api-keys`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) throw new Error('Failed to create API key');

  const result = await response.json();
  if (result.error) throw new Error(result.message || 'Failed to create API key');

  return result.data as CreatedApiKey;
}

export const useCreateApiKey = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ name }: { name?: string }) => createApiKey(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('API ključ je uspješno kreiran!');
    },
    onError: (error: any) => {
      console.error('Failed to create API key:', error);
      toast.error(error?.message || 'Greška pri kreiranju API ključa.');
    },
  });
};

export async function revokeApiKey(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api-keys/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });

  if (!response.ok) throw new Error('Failed to revoke API key');

  const result = await response.json();
  if (result.error) throw new Error(result.message || 'Failed to revoke API key');
}

export const useRevokeApiKey = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => revokeApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('API ključ je opozvan.');
    },
    onError: (error: any) => {
      console.error('Failed to revoke API key:', error);
      toast.error(error?.message || 'Greška pri opozivanju API ključa.');
    },
  });
};
